/**
 * feedback-collector.ts
 *
 * Ingestion Gate for Explicit and Implicit Feedback Signals.
 * Enforces the Reputation-to-Ingestion Matrix, Sanitizes correctionText,
 * and Calculates Multidimensional Interaction Quality Scores (Q).
 */

import {
  InteractionFeedback,
  FeedbackAdmissionVerdict,
  ReputationStanding,
  FeedbackRating,
  FeedbackReasonCode,
} from './learning-contracts.js';
import { PrivacySanitizer } from './privacy-sanitizer.js';
import { UserRecognitionEngine } from './user-recognition-engine.js';

export interface SubmitFeedbackParams {
  readonly requestId: string;
  readonly channelId: string;
  readonly tenantId?: string;
  readonly principalId: string;
  readonly sessionId: string;
  readonly rating?: FeedbackRating;
  readonly score?: number; // 1..5
  readonly reasonCodes?: readonly FeedbackReasonCode[];
  readonly correctionText?: string;
  readonly actionAccepted?: boolean;
  readonly actionEdited?: boolean;
  readonly actionCancelled?: boolean;
  readonly retryRequested?: boolean;
  readonly userFollowedUp?: boolean;
  readonly escalationRequested?: boolean;
}

export class FeedbackCollector {
  private readonly feedbacks: InteractionFeedback[] = [];
  private readonly hourlySubmissions = new Map<string, { count: number; windowStart: number }>();

  constructor(
    private readonly privacySanitizer: PrivacySanitizer = new PrivacySanitizer(),
    private readonly recognitionEngine: UserRecognitionEngine = new UserRecognitionEngine()
  ) {}

  /**
   * Evaluates admission limits against the user's reputation standing.
   */
  evaluateAdmission(principalId: string, tenantId: string = 'tenant-default'): FeedbackAdmissionVerdict {
    const profile = this.recognitionEngine.getProfile(principalId, tenantId);
    const standing = profile.reputationStanding;

    const rateLimits: Record<ReputationStanding, number> = {
      good: 20,
      probation: 5,
      restricted: 1,
      banned: 0,
    };

    const allowedHourlyRate = rateLimits[standing];
    const now = Date.now();
    const key = `${tenantId}:${principalId}`;
    let windowRecord = this.hourlySubmissions.get(key);

    if (!windowRecord || now - windowRecord.windowStart > 3600_000) {
      windowRecord = { count: 0, windowStart: now };
      this.hourlySubmissions.set(key, windowRecord);
    }

    if (standing === 'banned') {
      return {
        accepted: false,
        standing,
        allowedHourlyRate: 0,
        currentHourCount: windowRecord.count,
        eligibleForLearningArtifact: false,
        requiresElevatedReview: false,
        rejectionReason: '403 Forbidden: FeedbackSubmissionSuspended (Account Banned for Malicious Intent)',
      };
    }

    if (windowRecord.count >= allowedHourlyRate) {
      return {
        accepted: false,
        standing,
        allowedHourlyRate,
        currentHourCount: windowRecord.count,
        eligibleForLearningArtifact: false,
        requiresElevatedReview: false,
        rejectionReason: `429 Too Many Requests: Hourly limit of ${allowedHourlyRate} exceeded for reputation standing "${standing}"`,
      };
    }

    return {
      accepted: true,
      standing,
      allowedHourlyRate,
      currentHourCount: windowRecord.count,
      eligibleForLearningArtifact: standing === 'good' || standing === 'probation',
      requiresElevatedReview: standing === 'probation',
    };
  }

  /**
   * Submits user feedback with PII scrubbing and rate-limiting.
   */
  submitFeedback(params: SubmitFeedbackParams): {
    feedback: InteractionFeedback;
    verdict: FeedbackAdmissionVerdict;
    sanitizedCorrection?: string;
  } {
    const tenantId = params.tenantId || 'tenant-default';
    const verdict = this.evaluateAdmission(params.principalId, tenantId);

    if (!verdict.accepted) {
      throw new Error(verdict.rejectionReason);
    }

    // Increment rate counter
    const key = `${tenantId}:${params.principalId}`;
    const windowRecord = this.hourlySubmissions.get(key)!;
    windowRecord.count++;

    // Scrub correctionText if provided
    let sanitizedCorrection: string | undefined;
    if (params.correctionText) {
      const scan = this.privacySanitizer.sanitize(params.correctionText, params.sessionId);
      sanitizedCorrection = scan.sanitizedText;
    }

    const principalHmac = this.privacySanitizer.generateKeyVersionedHmac('analytics', params.principalId, tenantId);
    const sessionHmac = this.privacySanitizer.generateKeyVersionedHmac('session', params.sessionId, tenantId);

    const feedback: InteractionFeedback = {
      feedbackId: `fdb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      requestId: params.requestId,
      channelId: params.channelId,
      tenantId,
      principalHmac,
      sessionHmac,
      rating: params.rating,
      score: params.score,
      reasonCodes: params.reasonCodes || [],
      correctionText: sanitizedCorrection,
      actionAccepted: params.actionAccepted,
      actionEdited: params.actionEdited,
      actionCancelled: params.actionCancelled,
      retryRequested: params.retryRequested,
      userFollowedUp: params.userFollowedUp,
      escalationRequested: params.escalationRequested,
      createdAt: new Date().toISOString(),
      retentionClass: verdict.eligibleForLearningArtifact ? 'review' : 'metrics',
    };

    this.feedbacks.push(feedback);

    return {
      feedback,
      verdict,
      sanitizedCorrection,
    };
  }

  /**
   * Computes multidimensional composite quality score Q.
   * Q = 0.25G + 0.20R + 0.20T + 0.15E + 0.10U + 0.10L
   */
  calculateCompositeScore(metrics: {
    groundedness: number; // G (0..1)
    relevance: number; // R (0..1)
    toolCorrectness: number; // T (0..1)
    extractionValidity: number; // E (0..1)
    userSatisfaction: number; // U (0..1)
    latencyScore: number; // L (0..1)
  }): number {
    const Q =
      0.25 * metrics.groundedness +
      0.2 * metrics.relevance +
      0.2 * metrics.toolCorrectness +
      0.15 * metrics.extractionValidity +
      0.1 * metrics.userSatisfaction +
      0.1 * metrics.latencyScore;

    return Math.round(Q * 1000) / 1000;
  }

  getFeedbacks(): readonly InteractionFeedback[] {
    return [...this.feedbacks];
  }
}
